<template>
  <div class="map-preview">
    <div v-if="loadingOverlay" class="loading-overlay">
      <div class="spinner" />
    </div>

    <div class="map-preview__wrapper" :style="{ width }">
      <!-- Map -->
      <GmapMap
        id="map"
        ref="mapRef"
        map-type-id="roadmap"
        class="mc-map"
        :zoom="zoom"
        :center="center"
        :options="mapOptions"
        :style="{ height: '100%', width: '100%' }"
        @zoom_changed="handleZoomChanged"
      >
        <!-- Vehicle Marker -->
        <VehicleMarker
          v-if="selectedVehicle?.id"
          :id="selectedVehicle.id.toString()"
          :key="`marker-${selectedVehicle.id}`"
          :vehicle.sync="selectedVehicle"
          :active="fleetTaskStatus !== 'ENDED'"
          @click="handleClickVehicleIcon"
        />

        <GmapInfoWindow
          v-if="infoWindow.position.lat & infoWindow.position.lng"
          :position="infoWindow.position"
          :opened="infoWindow.status"
          :options="{
            ariaLabel: 'infoWindow',
          }"
        >
          <div class="infowindow-map">
            <div class="__header">
              <h6>{{ infoWindow.title }}</h6>
              <div class="__btn-close" @click="closePanelStopInfo">
                <i class="mc-fa icon-outline_close" />
              </div>
            </div>
            <div class="__body">
              <div class="__wrapper">
                <div class="__title">
                  {{ 'Waktu' }}
                </div>
                <div class="__content">
                  {{ infoWindow.date }}
                </div>
              </div>
              <div v-if="infoWindow.type === 'speed'" class="__wrapper">
                <div class="__title">
                  {{ 'Kecepatan' }}
                </div>
                <div class="__content">
                  {{ infoWindow.speed + ' Km/Jam' }}
                </div>
              </div>
              <div v-if="infoWindow.type === 'stop'" class="__wrapper"">
                <div class="__title">
                  {{ $t('global.field.stopDuration') }}
                </div>
                <div class="__content">
                  {{ infoWindow.totalHourStop }}
                </div>
              </div>
              <div class="__wrapper">
                <div class="__title">
                  {{ $t('global.field.address') }}
                </div>
                <div class="__content">
                  {{ infoWindow.address }}
                </div>
              </div>
            </div>
          </div>
        </GmapInfoWindow>

        <gmap-custom-marker
          v-for="(stopPosition, index) in stopPositions"
          :key="index"
          :marker="{
            lat: Number(stopPosition.position.lat),
            lng: Number(stopPosition.position.lng),
          }"
          :offsetY="40"
        >
          <div class="text-center">
            <img
              role="button"
              src="@/assets/images/caricon/stop-icon.svg"
              @click.stop="openPanelInfo(stopPosition)"
            />
          </div>
        </gmap-custom-marker>

        <!-- Points -->
        <GmapCluster :averageCenter="true" :zoom-on-click="true">
          <template v-for="(point, index) in points">
            <Geofence :key="`geofence-${index}`" :point="point" />

            <PointMarker
              :id="point.id"
              :key="`point-marker-${index}`"
              :number="getNumberPoint(index)"
              :icon="getPointIcon(point)"
              :marker="{ lat: point.lat, lng: point.lng }"
              :active="selectedPointIdx === index"
              :color="point.color"
              :address="point.name"
              :showLabel="showAddressMarkerLabel"
              @click="handleClickPointMarker(index)"
            />
          </template>
        </GmapCluster>

        <!-- Planned Route -->
        <GmapPolyline
          v-if="planTrip?.length !== 0 && isShowPlanTrip && !isLiveTrack"
          :path="planTrip"
          :options="{
            strokeColor: '#3D69A7',
            strokeOpacity: '1.0',
            strokeWeight: '6',
          }"
        />

        <!-- Actual Route -->
        <template
          v-if="
            ['STARTED', 'ENDED'].includes(fleetTaskStatus) &&
            actualTrips !== null &&
            isShowActualTrip
          "
        >
          <GmapPolyline
            v-if="isShowSpeedAndStop"
            v-for="(seg, index) in actualTrips"
            :key="`seg-${index}`"
            :path="seg.path"
            :options="{
              strokeColor: getColor(seg),
              strokeOpacity: 1.0,
              strokeWeight: 6,
              zIndex: 1,
            }"
            @click="openPanelInfo(seg, 'speed')"
          />
          <GmapPolyline
            v-else
            v-for="(route, index) in actualTrips"
            :key="`actual-route-${index}`"
            :path="route"
            :options="{
              strokeColor: '#429677',
              strokeOpacity: '1.0',
              strokeWeight: '6',
              zIndex: 1,
            }"
          />
        </template>

        <!-- PointPanel -->
        <PointPanel
          v-if="isPointEditable"
          :hasCreate="selectedPointIdx === null"
          :hasDelete="selectedPointIdx !== null"
          :point="selectedAddress"
          @click-create="handleAddPoint"
          @click-delete="handleRemovePoint"
          @close="handleClosePanel"
        />
      </GmapMap>

      <div
        v-if="!isMobileView && ['STARTED', 'ENDED'].includes(fleetTaskStatus)"
        class="map-preview__footer"
      >
        <div v-if="fleetTaskStatus === 'STARTED'" class="__group-address">
          <div class="__icon-wrapper">
            <i class="mc-icon icon-outline_live-view" />
          </div>
          <div class="__wrapper">
            <div class="__title">
              {{ $t('fleetTaskInstant.text.currentLocation') }}
              <span>
                ({{
                  $t('fleetTaskInstant.text.lastUpdatedOn', {
                    date: formatIndexDate(lastUpdateTime).date,
                    time: formatIndexDate(lastUpdateTime).time,
                  })
                }})
              </span>
            </div>
            <div class="__label">
              {{ address }}
            </div>
          </div>
        </div>

        <span v-else class="__last-update">
          {{
            $t('fleetTaskInstant.text.lastUpdatedOn', {
              date: formatIndexDate(lastUpdateTime).date,
              time: formatIndexDate(lastUpdateTime).time,
            })
          }}
        </span>

        <div v-if="isLiveTrack" class="__copyright">
          <span
            :hidden="
              getAsset('copyrightLogo', 'general') == null ||
              getAsset('copyrightLogo', 'general') == ''
            "
            >{{ $t('global.text.poweredBy') }} :
          </span>
          <figure class="d-inline-block mb-0 ml-2">
            <img
              ref="imgCopyrightLogo"
              height="16"
              :src="getAsset('copyrightLogo', 'general')"
              alt
            />
          </figure>
        </div>
      </div>

      <!-- Action Buttons -->
      <MapActionBtns
        v-if="!isLoading && !isStreetViewMode"
        :className="{
          '__last-update-attached': !isMobileView && ['STARTED', 'ENDED'].includes(fleetTaskStatus),
        }"
        :bottom-gap="computedActionBtnBottomGap"
        :isLiveTrack="isLiveTrack"
        :isStarted="fleetTaskStatus === 'STARTED'"
        @center="handleFocus"
      />

      <!-- Toggle Route -->
      <div
        v-if="isShowToggleTrip && !isLoading && !isLiveTrack && !isStreetViewMode"
        class="map-preview__toggle-route"
      >
        <div class="d-flex gap-2">
          <div class="__trip-legend">
            <span :style="{ backgroundColor: '#429677' }" />
          </div>
          <span class="flex-grow-1">{{ $t('fleetTaskInstant.text.actualRoute') }}</span>
          <McToggle v-model="isShowActualTrip" placement="left" class="__toggle" />
        </div>

        <div class="d-flex gap-2">
          <div class="__trip-legend">
            <span :style="{ backgroundColor: '#3D69A7' }" />
          </div>
          <span class="flex-grow-1">{{ $t('fleetTaskInstant.text.planRoute') }}</span>
          <McToggle v-model="isShowPlanTrip" placement="left" class="__toggle" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import store from '@/state/store'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { formatIndexDate, toUTC, getDuration } from '@/helpers/tzDate'

import { EventBus } from '../lib/event-bus.js'
import moment from 'moment'

import { gmapApi } from 'vue2-google-maps'

import GmapCustomMarker from 'vue2-gmap-custom-marker'
import McToggle from '@/components/McToggle'

import VehicleMarker from '../components/VehicleMarker'
import PointMarker from '../components/PointMarker'
import Geofence from '../components/Geofence'
import MapActionBtns from '../components/MapActionBtns'
import PointPanel from '../components/PointPanel'

import vehicleMappings from '../lib/constants/vehicle-mappings'
import { pointColors } from '../lib/constants/point-color-mappings'
import setMapOptions from '../lib/constants/map-options'
import { GOOGLE_MAP_ADDRESS_TYPE } from '../lib/constants/types'
import { simplifyPointsBasedOnZoom } from '../lib/helpers/simplify.js'

import { GetAddressData } from '@/services/api/map'
import { SHOW_VEHICLE, GET_SUMMARYTRIPS } from '@/services/api/fleetTaskInstant'
import { getAddress, getGeolocation } from '@/services/api/geolocation'
import { GET_DETAIL_TRIP_HISTORIES } from '@/services/api/fleet'

export default {
  components: {
    McToggle,
    VehicleMarker,
    PointMarker,
    Geofence,
    MapActionBtns,
    PointPanel,
    GmapCustomMarker,
  },
  mixins: [Helper, MixPanel],
  props: {
    width: {
      type: String,
      default: '40%',
    },
    showAddressMarkerLabel: {
      type: Boolean,
      default: false,
    },
    loadingOverlay: {
      type: Boolean,
      default: false,
    },
    isLiveTrack: {
      type: Boolean,
      default: false,
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isShowActualTrip: true,
      isShowPlanTrip: false,
      isLiveTrackDataReady: false,
      forceDisconnect: false,

      isStreetViewMode: false,

      points: [],
      actualTripsData: [],

      mapOptions: {},
      center: { lat: -2.958881, lng: 109.5543666 },

      activeMarker: null,
      address: null,

      // for point panel
      isSelectAddress: false,
      selectedAddress: {},

      zoom: 5,

      vehicleMappings,

      shortPollingInterval: null,

      stopPositions: [],

      infoWindow: {
        position: {
          lat: null,
          lng: null,
        },
        status: false,
        currentIndex: 0,
        totalStops: 0,
        title: null,
      },

      currentStopIndex: 0,
      stopPositionsAtCurrentLocation: [],
      lastUpdateTime: this.getCurrentDateTime(),

      detailTripHistories: null,
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', [
      'map',
      'showResponse',
      'listPointRes',
      'selectedPointIdx',
      'liveTrackResponse',
      'liveTrackVehicleResponse',
      'listVehicle',
      'selectedVehicle',
      'locationAllCoord',
      'locationVehicleWithNextDestination',
      'activeTab',
      'routeType',
    ]),
    ...mapGetters({
      selectedPoint: 'fleetTaskInstant/selectedPoint',
      getAsset: 'asset/getAsset',
    }),
    google: gmapApi,
    fleetTaskStatus() {
      if (this.showResponse?.status) {
        return this.showResponse.status.raw_type
      } else if (this.liveTrackResponse?.detail?.status) {
        return this.liveTrackResponse.detail.status.raw_type
      }

      return null
    },
    planTrip() {
      let data = this.showResponse?.planned_trip
      if (this.liveTrackResponse?.detail?.planned_trip) {
        data = this.liveTrackResponse.detail.planned_trip
      }

      const decodedRoute = data ? Helper.methods.decodePolyline6(data[0]) : []

      if (!decodedRoute || decodedRoute?.length === 0 || this.zoom >= 7) {
        return decodedRoute || []
      }

      const simplifiedPlanTrips = simplifyPointsBasedOnZoom(decodedRoute, this.zoom)

      return simplifiedPlanTrips
    },
    actualTrips() {
      if (!this.actualTripsData || this.actualTripsData?.length === 0 || this.zoom >= 7) {
        return this.actualTripsData || []
      }

      const simplifiedActualTrips = []

      this.actualTripsData.forEach((trip) => {
        const simplified = simplifyPointsBasedOnZoom(trip, this.zoom)
        simplifiedActualTrips.push(simplified)
      })

      return simplifiedActualTrips
    },
    totalPoints() {
      return this.points?.length
    },
    computedPoints() {
      if (!['STARTED', 'ENDED', 'CANCELED'].includes(this.fleetTaskStatus)) {
        return this.points
      }

      if (!this.points || this.points.length === 0) {
        return []
      }

      if (this.routeType === 'plan') {
        return this.points
      }

      const actualSequence = new Array(this.points.length)

      this.points.forEach((point, index) => {
        let actualIdx = index
        if (point.actualSequence !== null) {
          actualIdx = point.actualSequence - 1
        }

        actualSequence[actualIdx] = { ...point }
      })

      return actualSequence
    },
    hasStartPoint() {
      if (this.totalPoints === 0) return false

      return this.points?.[0]?.type === 'START'
    },
    hasEndPoint() {
      if (this.totalPoints === 0) return false

      return this.points?.[this.totalPoints - 1]?.type === 'END'
    },
    isShowToggleTrip() {
      return ['STARTED', 'ENDED'].includes(this.fleetTaskStatus)
    },
    isPointEditable() {
      return (
        ['point', 'address'].includes(this.activeMarker) &&
        !this.isLiveTrack &&
        !['STARTED', 'ENDED'].includes(this.fleetTaskStatus)
      )
    },
    computedActionBtnBottomGap() {
      if (this.isMobileView) {
        return '10px'
      }

      if (this.fleetTaskStatus === 'STARTED') {
        return '82px'
      } else if (this.fleetTaskStatus === 'ENDED') {
        return '52px'
      }

      return '10px'
    },
    isShowSpeedAndStop() {
      return (
        this.isLiveTrack
      )
    },
  },
  watch: {
    fleetTaskStatus: {
      async handler(newValue) {
        if (
          this.fleetTaskStatus &&
          !['STARTED', 'ENDED'].includes(this.fleetTaskStatus) &&
          !this.shortPollingInterval
        ) {
          this.startShortPolling()
        }

        if (newValue === 'ENDED') {
          this.isShowPlanTrip = false
          this.actualTripsData = await this.getDecodedActualTrips()
          this.lastUpdateTime = this.isLiveTrack
            ? this.liveTrackResponse.detail.updated_on
            : this.showResponse.updated_on

          return
        }

        this.isShowPlanTrip = true
      },
      immediate: true,
    },
  },
  created() {
    if (this.$socket !== undefined) {
      this.$socket.close()
    }

    if (this.fleetTaskStatus !== 'ENDED') {
      this.isShowPlanTrip = true
    }

    EventBus.$on('update-map', this.updateMap)
    EventBus.$on('update-address', this.getCurrentLocationAddress)
    EventBus.$on('focus-point', this.handleFocusPoint)
    EventBus.$on('success-fetch-live-track-data', this.updateMapLiveTrack)
    EventBus.$on('handle-focus-map-view', this.handleFocusMapView)
    EventBus.$on('set-trip-toggle', this.handleSetTripToggle)
  },
  async mounted() {
    EventBus.$on('live-track-data-ready', this.onLiveTrackDataReady)

    if (!this.isLiveTrackDataReady && this.isLiveTrack) {
      await new Promise((resolve) => {
        EventBus.$once('live-track-data-ready', resolve)
      })
    }

    await this.$nextTick()

    await this.initMap()
    await this.$nextTick()

    this.isLoading = false
  },
  beforeDestroy() {
    this.forceDisconnect = true

    EventBus.$off('update-map', this.updateMap)
    EventBus.$off('update-address', this.getCurrentLocationAddress)
    EventBus.$off('focus-point', this.handleFocusPoint)
    EventBus.$off('success-fetch-live-track-data', this.updateMapLiveTrack)
    EventBus.$off('live-track-data-ready', this.onLiveTrackDataReady)
    EventBus.$off('handle-focus-map-view', this.handleFocusMapView)
    EventBus.$off('set-trip-toggle', this.handleSetTripToggle)

    if (this.shortPollingInterval) {
      clearInterval(this.shortPollingInterval)
    }
  },
  methods: {
    formatIndexDate,
    getDuration,
    handleSetTripToggle(type) {
      if (this.isShowActualTrip && this.isShowPlanTrip) return

      if (type === 'actual' && !this.isShowActualTrip) {
        this.isShowActualTrip = true
        this.isShowPlanTrip = false
      } else if (type === 'plan' && !this.isShowPlanTrip) {
        this.isShowActualTrip = false
        this.isShowPlanTrip = true
      }
    },

    getCurrentDateTime() {
      const now = new Date()
      const isoDateTime = now.toISOString()
      return isoDateTime
    },

    /**
     * Get address from vehicle lat lng
     * @param {number} lat: latitude
     * @param {number} lon: longitude
     */
    async getCurrentLocationAddress({ lat, lng }) {
      if (!this.isLiveTrack) {
        const res = await getGeolocation({ lat, lon: lng })

        const address = res.get(`${lat},${lng}`)

        if (address === undefined) return
        this.address = address.name

        return
      }

      const useCache = store.state.identity.featureFlag.find(
        (feature) => feature.key == 'cache_geolocation'
      ).value
      if (useCache) {
        const address = await getGeolocation({ lat, lng })
        this.address = getAddress(address, lat, lng)?.name
      } else {
        const address = await GetAddressData(lat, lng)
        this.address = address.display_name
      }
    },

    async getVehicleData(vehicleId) {
      const vehicleData = await this.$apollo.query({
        query: SHOW_VEHICLE,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            vehicleId: parseInt(vehicleId),
          },
        },
      })

      this.mappedVehicleData(vehicleData.data.vehicleStatus.vehicleStatus)

      this.getCurrentLocationAddress({
        lat: this.selectedVehicle.latitude,
        lng: this.selectedVehicle.longitude,
      })

      await this.$nextTick()
    },

    mappedVehicleData(vehicleObj) {
      if (!vehicleObj) return

      this.$store.dispatch('fleetTaskInstant/setSelectedVehicle', {
        id: vehicleObj.vehicleId,
        latitude: vehicleObj.latitude,
        longitude: vehicleObj.longitude,
        licensePlate: vehicleObj.licensePlate,
        direction: vehicleObj.direction,
        calculatedMotionStatus: vehicleObj.calculatedMotionStatus,
        icon: vehicleObj.vehicle.vehicleType.icon,
        status:
          this.listVehicle?.[vehicleObj.vehicleId]?.fleet_task === null
            ? 'available'
            : 'not_available',
      })
    },

    async initMap() {
      if (this.map !== null) return

      const mapRef = this.$refs.mapRef
      if (mapRef === undefined) return

      const self = this

      await mapRef.$mapPromise.then((map) => {
        self.$store.dispatch('fleetTaskInstant/setMap', map)
        self.mapOptions = setMapOptions(self.google)

        const streetView = map.getStreetView()
        streetView.addListener('visible_changed', () => {
          self.isStreetViewMode = streetView.getVisible()
        })

        // don't add listener if the status is STARTED or ENDED
        if (['STARTED', 'ENDED'].includes(this.fleetTaskStatus)) return

        // TODO: refine the logic to click map
        map.addListener('click', async (event) => {
          event.stop()

          if (this.activeMarker == 'point' && !this.isSelectAddress) {
            this.isSelectAddress = true
            return
          }

          if (this.isSelectAddress) {
            this.isSelectAddress = false
            this.activeMarker = null
            this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', null)

            return
          }

          const lat = event.latLng.lat()
          const lng = event.latLng.lng()

          const address = await GetAddressData(lat, lng)

          this.selectedAddress = {
            id: Date.now().toString(),
            name: address?.name ?? '', // TODO: enhancement for name location using value inside props address
            fullName: address.display_name,
            lat: address.lat,
            lng: address.lon,
          }

          this.isSelectAddress = true
          this.activeMarker = 'address'
        })
      })

      let eventName = 'Map Loaded on Quick Assignment Detail'

      if (this.isLiveTrack) {
        eventName = 'Map Loaded on Public View Quick Assignment'
      }

      this.track(eventName)
      await this.$nextTick()
    },

    async startWebSocket() {
      const self = this
      const obj = {
        type: 'update_tags',
        data: localStorage.getItem('payload'),
      }

      // Connect web socket
      await this.$store.dispatch(
        'ws/WSConnect',
        `${process.env.VUE_APP_WS_URL}/ws?token=${localStorage.getItem('wsToken')}`
      )
      this.$socket.onopen = () => {
        this.$socket.sendObj(obj)
      }

      this.$socket.onmessage = (data) => {
        const signal = JSON.parse(data.data)

        // Only process data of signal type is gps
        if (!signal.type?.startsWith('gps-')) return

        const payload = signal.payload

        if (payload.vehicleId !== this.selectedVehicle?.id && !this.selectedVehicle?.id) return

        self.$set(this.selectedVehicle, 'latitude', payload.latitude)
        self.$set(this.selectedVehicle, 'longitude', payload.longitude)

        this.getCurrentLocationAddress({ lat: payload.latitude, lng: payload.longitude })
      }

      this.$socket.onclose = async () => {
        if (this.forceDisconnect) return
        this.startWebSocket()
      }
    },

    startShortPolling() {
      clearInterval(this.shortPollingInterval)

      const self = this
      this.shortPollingInterval = setInterval(() => {
        const vehicleId = self.isLiveTrack
          ? self.liveTrackVehicleResponse?.vehicleStatus.vehicleId
          : self.selectedVehicle?.id

        self.getVehicleData(vehicleId)
      }, 10000)
    },

    handleZoomChanged(event) {
      this.zoom = event
    },

    setCenter({ lat, lng, zoom = 15 }) {
      const latlng = new this.google.maps.LatLng(lat, lng)
      this.map.setCenter(latlng)
      this.map.setZoom(zoom)
    },

    async fitBounds(locations) {
      if (locations.length === 0) return

      await this.$nextTick()

      let bounds = new this.google.maps.LatLngBounds()
      locations.forEach((location) => {
        bounds.extend(new this.google.maps.LatLng(location.lat, location.lng))

        this.map.fitBounds(bounds)
      })
    },

    async updateMap(type = 'init') {
      let locations = []

      if (this.showResponse?.status?.raw_type === 'ENDED') {
        this.$store.dispatch('fleetTaskInstant/setSelectedVehicle', {})
      }

      if (
        this.showResponse?.vehicle.id &&
        !['ENDED', 'CANCELED'].includes(this.showResponse?.status?.raw_type)
      ) {
        const [_, decodedActualTrips] = await Promise.all([
          this.getVehicleData(this.showResponse?.vehicle.id),
          this.getDecodedActualTrips(),
        ])

        this.actualTripsData = decodedActualTrips

        if (this.selectedVehicle?.id) {
          locations.push({
            lat: this.selectedVehicle.latitude,
            lng: this.selectedVehicle.longitude,
          })
        }
      }

      // Add points as locations to fit bound map
      if (this.listPointRes?.points?.length > 0) {
        const points = []
        await this.$nextTick()

        this.listPointRes?.points.forEach((point) => {
          const formatted = {
            id: point.id,
            type: point.type,
            name: point.address.name,
            fullName: point.address.full_name,
            lat: point.address.geolocation.coordinate.latitude,
            lng: point.address.geolocation.coordinate.longitude,
            status: point.status.raw_type,
            geofence: {
              type: point.address.geolocation.type,
              radius: point.address.geolocation.radius,
              polygon: point.address.geolocation.polygon,
            },
            color: '#1c74da',
          }

          if (['STARTED', 'ENDED'].includes(this.fleetTaskStatus)) {
            formatted.color = pointColors[point.status.raw_type]
          }

          const geolocation = point.address.geolocation

          // NOTE: variable can be in Pascal convention
          formatted.lat = geolocation.coordinate?.latitude || geolocation.Coordinate?.Latitude
          formatted.lng = geolocation.coordinate?.longitude || geolocation.Coordinate?.Longitude

          points.push(formatted)
          locations.push(formatted)
        })

        this.points = points
        await this.$nextTick()

        if (this.isShowPlanTrip) {
          this.planTrip.forEach((item) => {
            locations.push(item)
          })
        }

        if (this.isShowActualTrip && this.actualTrips?.length > 0) {
          this.actualTrips.forEach((chunkedRoute) => {
            chunkedRoute.forEach((item) => {
              locations.push(item)
            })
          })
        }
      }

      if (
        ['STARTED', 'ENDED'].includes(this.showResponse?.status?.raw_type) &&
        store.getters['identity/getFeatureFlagData']('quickAssignmentShowIdleHistory').value
      ) {
        this.$nextTick(() => {
          this.loadStopPositions()
        })
      }

      if (type !== 'init' || locations?.length === 0) return

      this.fitBounds(locations)
    },

    async updateMapLiveTrack(type = 'init') {
      const locationVehicleWithNextDestination = []
      const locationAllCoord = []

      const isEndedOrCanceled = ['ENDED', 'CANCELED'].includes(
        this.liveTrackResponse?.detail.status.raw_type
      )

      if (isEndedOrCanceled) {
        this.$store.dispatch('fleetTaskInstant/setSelectedVehicle', {})
      }

      if (this.liveTrackVehicleResponse?.vehicleStatus.vehicleId && !isEndedOrCanceled) {
        const [_, decodedActualTrips] = await Promise.all([
          this.mappedVehicleData(this.liveTrackVehicleResponse?.vehicleStatus),
          this.getDecodedActualTrips(),
        ])

        this.actualTripsData = decodedActualTrips

        if (!isEndedOrCanceled && this.selectedVehicle?.id) {
          locationVehicleWithNextDestination.push({
            lat: this.selectedVehicle.latitude,
            lng: this.selectedVehicle.longitude,
          })
          locationAllCoord.push({
            lat: this.selectedVehicle.latitude,
            lng: this.selectedVehicle.longitude,
          })
        }
      }

      if (this.liveTrackResponse?.detail?.current_point_location) {
        const point = this.liveTrackResponse?.detail?.current_point_location
        locationVehicleWithNextDestination.push({
          lat: point.address.geolocation.coordinate.latitude,
          lng: point.address.geolocation.coordinate.longitude,
        })
      }

      // Add points as locations to fit bound map
      if (this.liveTrackResponse?.points?.length > 0) {
        const points = []
        await this.$nextTick()

        this.liveTrackResponse?.points.forEach((point) => {
          const formatted = {
            id: point.id,
            type: point.type,
            name: point.address.name,
            fullName: point.address.full_name,
            lat: point.address.geolocation.coordinate.latitude,
            lng: point.address.geolocation.coordinate.longitude,
            geofence: {
              type: point.address.geolocation.type,
              radius: point.address.geolocation.radius,
              polygon: point.address.geolocation.polygon,
            },
            color: '#1c74da',
          }

          if (['STARTED', 'ENDED'].includes(this.fleetTaskStatus)) {
            formatted.color = pointColors[point.status.raw_type]
          }

          const geolocation = point.address.geolocation

          // NOTE: variable can be in Pascal convention
          formatted.lat = geolocation.coordinate?.latitude || geolocation.Coordinate?.Latitude
          formatted.lng = geolocation.coordinate?.longitude || geolocation.Coordinate?.Longitude

          points.push(formatted)
          locationAllCoord.push(formatted)
        })

        this.points = points
        await this.$nextTick()

        if (this.isShowPlanTrip) {
          this.planTrip.forEach((item) => {
            locationAllCoord.push(item)
          })
        }

        if (this.isShowActualTrip && this.actualTrips?.length > 0) {
          this.actualTrips.forEach((chunkedRoute) => {
            chunkedRoute.forEach((item) => {
              locationAllCoord.push(item)
            })
          })
        }

        this.$store.dispatch('fleetTaskInstant/setLocationAllCoord', locationAllCoord)
        this.$store.dispatch(
          'fleetTaskInstant/setLocationVehicleWithNextDestination',
          locationVehicleWithNextDestination
        )

        if (
          ['STARTED', 'ENDED'].includes(this.liveTrackResponse?.detail?.status?.raw_type) &&
          this.isShowSpeedAndStop
        ) {
          this.$nextTick(() => {
            this.getTripHistories()
          })
        }

        if (
          type !== 'init' ||
          (locationVehicleWithNextDestination.length === 0 && this.fleetTaskStatus === 'STARTED')
        )
          return

        await this.$nextTick()
        if (this.fleetTaskStatus === 'STARTED') {
          this.fitBounds(locationVehicleWithNextDestination)
        } else {
          this.handleFocusMapView()
        }
      }
    },

    async getDecodedActualTrips() {
      let data = this.showResponse?.actual_trip
      if (this.liveTrackResponse?.detail?.actual_trip) {
        data = this.liveTrackResponse.detail.actual_trip
      }

      if (data?.length === 0) {
        this.actualTripsData = []
        return
      }

      return data.map((route) => Helper.methods.decodePolyline6(route))
    },

    getNumberPoint(index) {
      if (this.hasStartPoint) {
        return index
      }

      return index + 1
    },

    getPointIcon(point) {
      if (this.isLiveTrack || this.routeType === 'plan' || point.type !== 'DEFAULT') {
        return point.type
      }

      if (['VISITING', 'VISITED'].includes(point.status)) {
        return point.type
      }

      return 'MARKER'
    },

    handleFocusPoint(isFocus = false) {
      if (!isFocus && !this.isLiveTrack) {
        const locations = []
        if (this.selectedVehicle?.id) {
          locations.push({
            lat: this.selectedVehicle.latitude,
            lng: this.selectedVehicle.longitude,
          })
        }

        this.fitBounds([...this.points, ...locations])

        return
      }

      this.setCenter({
        lat: this.selectedPoint.address.geolocation.coordinate.latitude,
        lng: this.selectedPoint.address.geolocation.coordinate.longitude,
      })
    },

    handleClickVehicleIcon() {
      if (!this.selectedVehicle?.id) return

      if (this.isLiveTrack) {
        this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', null)
      }

      this.setCenter({
        lat: this.selectedVehicle.latitude,
        lng: this.selectedVehicle.longitude,
      })
    },

    async handleClickPointMarker(index) {
      this.activeMarker = 'point'
      this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', index)
      await this.$nextTick()

      if (['TEMPORARY', 'DRAFT', 'SCHEDULED'].includes(this.fleetTaskStatus)) {
        this.selectedAddress = { ...this.points[this.selectedPointIdx] }
      }

      EventBus.$emit('focus-point', true)
    },

    handleAddPoint() {
      const address = {
        type: GOOGLE_MAP_ADDRESS_TYPE,
        name: this.selectedAddress.name || this.selectedAddress.fullName,
        full_name: this.selectedAddress.fullName,
        lat: this.selectedAddress.lat,
        lng: this.selectedAddress.lng,
      }

      EventBus.$emit('add-new-point', address)

      this.activeMarker = null
      this.isSelectAddress = false
      this.selectedAddress = {}
    },

    async handleRemovePoint() {
      EventBus.$emit('remove-point', this.selectedPointIdx)

      this.activeMarker = null
      this.selectedAddress = {}
      this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', null)
      await this.$nextTick()
    },

    onLiveTrackDataReady() {
      this.isLiveTrackDataReady = true
    },

    handleFocusMapView() {
      this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', null)
      if (this.activeTab === 'vehicle') {
        let bounds = new this.google.maps.LatLngBounds()
        this.locationVehicleWithNextDestination.forEach((location) => {
          bounds.extend(new this.google.maps.LatLng(location.lat, location.lng))
        })

        this.map.fitBounds(bounds)
      } else {
        let bounds = new this.google.maps.LatLngBounds()
        this.locationAllCoord.forEach((location) => {
          bounds.extend(new this.google.maps.LatLng(location.lat, location.lng))
        })

        this.map.fitBounds(bounds)
      }
    },

    handleFocus() {
      if (this.isLiveTrack) {
        this.handleFocusMapView()
      } else {
        this.handleFocusPoint()
      }
    },

    async loadStopPositions() {
      try {
        if (['STARTED', 'ENDED'].includes(this.showResponse?.status?.raw_type)) {
          let endDate = this.now()
          if (this.showResponse?.actual_arrival_on) {
            endDate = this.momentToISOString(this.showResponse?.actual_arrival_on)
          }

          const res = await this.$apollo.query({
            query: GET_SUMMARYTRIPS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                vehicleId: parseInt(this.showResponse?.vehicle?.id),
                endDate: endDate,
                pagination: {
                  page: 1,
                  show: 99999,
                },
              },
            },
          })

          if (!res.data.summaryTrips.isSuccessful) {
            this.$mcToast.error('Terjadi kesalahan.')
            return
          }

          const summaryTrips = res.data.summaryTrips.summaryTrips.summaryTrips

          const actualDepartureTime = moment(this.showResponse?.actual_departure_on).utc()

          this.stopPositions = []

          for (let i = 0; i < summaryTrips.length - 1; i++) {
            const trip = summaryTrips[i]
            const prevTrip = summaryTrips[i + 1]
            const startTime = moment(trip.startTime)

            if (actualDepartureTime.isSameOrAfter(startTime)) break

            let stopTime = moment(prevTrip.stopTime)
            if (actualDepartureTime.isSameOrAfter(stopTime)) {
              stopTime = actualDepartureTime
            }

            const address = await GetAddressData(trip.startLat, trip.startLong)
            const totalSeconds = startTime.diff(stopTime, 'seconds')

            const diffHours = Math.floor(totalSeconds / 3600)
            const diffMinutes = Math.floor((totalSeconds % 3600) / 60)
            const diffSeconds = totalSeconds % 60

            const formattedDiff = [
              diffHours.toString().padStart(2, '0'),
              diffMinutes.toString().padStart(2, '0'),
              diffSeconds.toString().padStart(2, '0'),
            ].join(':')

            this.stopPositions.unshift({
              position: {
                lat: trip.startLat,
                lng: trip.startLong,
              },
              totalHourStop: formattedDiff,
              address: address.display_name,
              date: this.formattedDateRange(stopTime.local(), startTime),
            })
          }
        }
      } catch (e) {
        this.$mcToast.error('Terjadi kesalahan.')
      }
    },

    async openPanelInfo(data, type = 'stop') {
      if (type === 'speed') {
        const address = await GetAddressData(data.path[0].lat, data.path[0].lng)
        this.infoWindow = {
          position: {
            lat: data.path[0].lat,
            lng: data.path[0].lng,
          },
          speed: data.speed,
          address: address.display_name,
          date: `${this.formatIndexDate(data.sentOn).date}, ${
            this.formatIndexDate(data.sentOn).time
          }`,
          status: true,
          title: 'Kecepatan',
          type: type,
        }
        return
      }

      this.infoWindow = {
        ...data,
        status: true,
        title: 'Berhenti Kendaraan',
        type: type,
      }
    },

    closePanelStopInfo() {
      this.infoWindow.status = false
    },

    formattedDateRange(start, end) {
      if (start.isSame(end, 'day')) {
        return `${start.format('DD MMM YYYY, HH:mm')} - ${end.format('HH:mm')}`
      } else {
        return `${start.format('DD MMM YYYY, HH:mm')} - ${end.format('DD MMM YYYY, HH:mm')}`
      }
    },
    handleClosePanel() {
      this.activeMarker = null
    },
    async getTripHistories() {
      const vehicleId = this.liveTrackResponse.detail?.vehicle?.id
      const status = this.liveTrackResponse?.detail?.status?.raw_type
      const startDate = this.liveTrackResponse?.detail?.actual_departure_on
      let endDate = this.now()
      if (status === 'ENDED') {
        endDate = this.liveTrackResponse?.detail?.actual_arrival_on
      }
      const res = await this.$apollo.query({
        query: GET_DETAIL_TRIP_HISTORIES,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            vehicleId: parseInt(vehicleId),
            startDate: toUTC(startDate),
            endDate: toUTC(endDate),
          },
        },
      })
      this.detailTripHistories = res.data.detailTripHistories.detailTripHistories

      this.transformStopTrip(this.detailTripHistories.stoppage)
      this.transformTripHistories(this.detailTripHistories.data)
    },
    async transformStopTrip(stoppage) {
      const result = []

      const data = this.fleetTaskStatus !== 'ENDED' ? stoppage.slice(0, -1) : stoppage

      for (const item of data) {
        const address = await GetAddressData(item.stopLat, item.stopLong)

        result.push({
          position: {
            lat: item.stopLat,
            lng: item.stopLong,
          },
          totalHourStop: this.getDuration(item.startDate, item.endDate).duration,
          address: address.display_name,
          date: this.getDuration(item.startDate, item.endDate).range,
        })
      }

      this.stopPositions = result
    },

    transformTripHistories(tripHistories) {
      const result = []
      let prev = null

      tripHistories.forEach((t) => {
        const lat = Number(t.latitude)
        const lng = Number(t.longitude)

        if (!prev) {
          prev = { lat, lng, speed: t.speed }
          return
        }

        const isMoved = lat !== prev.lat || lng !== prev.lng

        if (isMoved) {
          result.push({
            path: [
              { lat: prev.lat, lng: prev.lng },
              { lat, lng },
            ],
            speed: t.speed,
            sentOn: t.sentOn,
          })
        }

        prev = { lat, lng, speed: t.speed, sentOn: t.sentOn }
      })
      this.actualTripsData = result
    },
    getColor(seg) {
      if (!this.isShowSpeedAndStop) {
        return '#429677'
      }

      const s = seg.speed
      let color = '#429677'

      if (s >= 0 && s <= 20) {
        color = '#f41d00'
      } else if (s > 20 && s <= 40) {
        color = '#f64a33'
      } else if (s > 40 && s <= 60) {
        color = '#ffa133'
      } else if (s > 60 && s <= 80) {
        color = '#71b099'
      } else if (s > 80) {
        color = '#137c55'
      }

      return color
    },
  },
}
</script>

<style lang="scss" scoped>
.map-preview {
  position: absolute;
  display: flex;
  flex-direction: row-reverse;
  width: 100%;
  height: 100%;

  &__wrapper {
    position: relative;
    height: 100%;
    z-index: 0;

    display: flex;
    flex-direction: column;

    :deep([aria-label='infoWindow']) {
      padding: 0 !important;

      .gm-style-iw-chr {
        display: none;
      }

      .gm-style-iw-d {
        display: contents;

        .infowindow-map {
          background: $mep-color-text-400;
          padding: 12px;
          color: $white !important;
          max-width: 300px;

          .__header {
            display: flex;
            justify-content: space-between;
            font-size: 13px;

            border-bottom: 1px solid $mep-color-text-200;
            margin-bottom: 10px;

            h6 {
              color: $white;
            }

            .__btn-close {
              padding: 3px;
              cursor: pointer;
              height: fit-content;

              .mc-fa::before {
                font-size: 13px;
                color: $white;
              }
            }
          }

          .__body {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .__title {
              color: $mep-color-text-200;
            }
          }
        }
      }
    }

    :deep(.gm-style-iw-tc) {
      display: none;
    }

    :deep(.gm-ui-hover-effect) > span {
      background-color: #000;
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-content: center;
    background-color: $white;
    border: 1px solid $mep-primary-100;
    border-left: none;

    .__group-address {
      padding: 1rem;
      display: flex;
      gap: 10px;

      .__icon-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        background: $cyan-100;
        width: 40px;
        height: 40px;
        border-radius: 24px;
        padding: 10px;

        .mc-icon {
          font-size: 24px;
        }
      }

      .__wrapper {
        .__title {
          font-size: 11px;
          color: $mep-color-text;
          font-weight: 600;

          span {
            color: $mep-color-text-300;
            font-weight: 500;
            font-style: italic;
          }
        }

        .__label {
          font-size: 11px;
          color: $mep-color-text-300;
        }
      }
    }

    .__last-update {
      padding: 12px 16px;
      font-size: 11px;
      color: $mep-color-text-300;
      font-weight: 500;
      font-style: italic;
    }

    .__copyright {
      font-size: 10px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  &__toggle-route {
    position: absolute;
    z-index: 1;
    top: 20px;
    left: 20px;
    background-color: $white;
    padding: 8px;
    border: 1px solid $mep-primary-100;
    border-radius: 8px;
    box-shadow: 0px 4px 8px 0px #10184029;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .__trip-legend {
      flex-shrink: 1;

      span {
        display: inline-block;
        margin: auto;
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }
    }
  }

  :deep(.mc-map) {
    .gm-bundled-control {
      .gmnoprint {
        top: -50px !important;
      }

      .gm-svpc {
        top: 22px !important;
      }
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  .spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid $mep-primary;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 2s linear infinite;
  }

  .loading-logo {
    max-width: 100px;
    max-height: 100px;

    &.blinking {
      animation: blink 1s infinite;
    }
  }
}
</style>
